import { useState, useEffect, useRef } from "react";
import { logo, progressTick, unView, view } from "../assets/images/images";
import CheckInput from "../assets/images/CheckInput";
import { getCountries } from "../services/api/common.service";
import { signup } from "../services/api/signup.service";
import { useNavigate } from "react-router-dom";
import { validateEmail, validatePassword } from "../share/validation";
import CheckIconWhite from "../assets/images/CheckIconWhite";
import BackIcon from "../assets/images/BackIcon";
import { showApiError } from "../services/apiError";
import { coreFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from "../services/fieldLimits";
import FieldCounter from "../component/FieldCounter";



interface Country {
    id: string;
    code: string;
    name: string;
}
// Server property -> form field: the nested DTO paths and the entity column names.
const SIGNUP_FIELD_ALIASES: Record<string, string> = {
    "accountDetails.firstName": "firstName", "accountDetails.lastName": "lastName", "accountDetails.fullName": "firstName",
    "accountDetails.email": "email", "accountDetails.password": "password",
    "companyDetails.companyName": "companyName", "companyDetails.companyNo": "companyNo", "companyDetails.countryId": "country",
    emailAddress: "email", companyNumber: "companyNo", countryId: "country",
};
const SIGNUP_FIELD_STEPS: Record<string, number> = { firstName: 1, lastName: 1, email: 1, password: 1, companyName: 2, companyNo: 2, country: 2 };
// What the API stores: NFC, any run of whitespace as one space, trimmed.
const tidy = (value: string) => value.normalize("NFC").replace(/[\s\u00a0]+/g, " ").trim();
// 16px on phones so iOS does not zoom into the field; 14px from sm up as before.
const INPUT_TEXT = "!text-[16px] sm:!text-[14px]";

const Signup = () => {
    const [showPassword, setShowPassword] = useState<{ password: boolean; confirmPassword: boolean; }>({
        password: false,
        confirmPassword: false,
    });
    const nav = useNavigate();
    const [formValues, setFormValues] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        agreeToTerms: false,
        companyName: "",
        companyNo: "",
        country: ""
    });
    const [step, setStep] = useState<string>("1");
    const [countries, setCountries] = useState<Country[]>([]);
    const [errors, setErrors] = useState({
        confirmPassword: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        companyName: '',
        companyNo: '',
        country: ''
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [highlightedCountry, setHighlightedCountry] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [apiError, setApiError] = useState<string>('');
    const language = window.languageContext?.language;
    const t = (key: string) => language?.[key] ?? key;
    const limits = useFieldLimits(coreFormLimits("signup"));

    useEffect(() => {
        const fetchCountries = async () => {
            const data = await getCountries();
            setCountries(data);
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsCountryDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleCountrySelect = (countryId: string, countryName: string) => {
        setFormValues(prev => ({ ...prev, country: countryId }));
        setSearchTerm(countryName);
        setIsCountryDropdownOpen(false);
        setHighlightedCountry(-1);
        setErrors(prev => ({ ...prev, country: '' }));
    };
    const matchingCountries = countries.filter((country) => country.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const exactCountry = (text: string) => countries.find((country) => country.name.toLowerCase() === tidy(text).toLowerCase());
    const countryListRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (highlightedCountry >= 0) countryListRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
    }, [highlightedCountry]);
    // Arrows move through the list, Enter picks, Escape closes; Enter with the list closed submits.
    const handleCountryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setIsCountryDropdownOpen(true);
            const step = e.key === "ArrowDown" ? 1 : -1;
            // No matches: nothing to highlight, so aria-activedescendant never names a missing option.
            setHighlightedCountry((current) => matchingCountries.length === 0 ? -1 : Math.max(0, Math.min(matchingCountries.length - 1, current + step)));
        } else if (e.key === "Enter" && isCountryDropdownOpen && matchingCountries.length > 0) {
            e.preventDefault();
            // The highlighted row, else the exact name typed, else the only match; otherwise show what Enter would pick.
            const picked = highlightedCountry >= 0
                ? matchingCountries[highlightedCountry]
                : exactCountry(searchTerm) ?? (matchingCountries.length === 1 ? matchingCountries[0] : undefined);
            if (picked) handleCountrySelect(picked.id, picked.name);
            else setHighlightedCountry(0);
        } else if (e.key === "Escape" && isCountryDropdownOpen) {
            e.preventDefault();
            setIsCountryDropdownOpen(false);
        }
    };
    // Names keep inner spaces while typing and are tidied when the field is left.
    const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormValues(prev => ({ ...prev, [name]: tidy(value) }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
        const { name, value, type } = e.target;
        if (type === "checkbox") {
            const checked = (e.target as HTMLInputElement).checked;
            setFormValues(prev => ({ ...prev, [name]: checked }));
        } else {
            if (name !== 'firstName' && name !== 'lastName' && name !== 'companyName') {
                setFormValues(prev => ({ ...prev, [name]: value.trim() }));
            } else {
                if (value.trim() === '') {
                    setFormValues(prev => ({ ...prev, [name]: value.trim() }));
                } else {
                    setFormValues(prev => ({ ...prev, [name]: value }));
                }
            }

            if (name === 'firstName' || name === 'lastName') {
                const label = name === 'firstName' ? 'First name is required' : 'Last name is required';
                setErrors(prev => ({ ...prev, [name]: value.trim() ? '' : t(label) }));
            }

            if (name === 'email') {
                if (!value) {
                    setErrors(prev => ({ ...prev, email: 'Email is required' }));
                } else if (!validateEmail(value)) {
                    setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
                } else {
                    setErrors(prev => ({ ...prev, email: '' }));
                }
            }

            if (name === 'password') {
                const passwordError = validatePassword(value);
                setErrors(prev => ({ ...prev, password: passwordError }));
                // if (formValues.password && value !== formValues.password) {
                //     setErrors(prev => ({ ...prev, password: 'Passwords do not match' }));
                // } else if (formValues.password) {
                //     setErrors(prev => ({ ...prev, password: '' }));
                // }
            }

            if (name === 'confirmPassword' || name === 'password') {
                const passwordValue = name === 'password' ? value : formValues.password;
                const confirmValue = name === 'confirmPassword' ? value : formValues.confirmPassword;

                if (confirmValue && passwordValue !== confirmValue) {
                    setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
                } else {
                    setErrors(prev => ({ ...prev, confirmPassword: '' }));
                }
            }

            if (name === 'companyName') {
                if (!value.trim()) {
                    setErrors(prev => ({ ...prev, companyName: t('Company Name is required') }));
                } else {
                    setErrors(prev => ({ ...prev, companyName: '' }));
                }
            }

            if (name === 'companyNo') {
                if (!value.trim()) {
                    setErrors(prev => ({ ...prev, companyNo: t('Company Number is required') }));
                } else {
                    setErrors(prev => ({ ...prev, companyNo: '' }));
                }
            }

            if (name === 'country') {
                if (!value) {
                    setErrors(prev => ({ ...prev, country: t('Country is required') }));
                } else {
                    setErrors(prev => ({ ...prev, country: '' }));
                }
            }
        }
    };
    const isFormStepOne = () => {
        const { firstName, lastName, email, password, confirmPassword, agreeToTerms } = formValues;
        return tidy(firstName) && tidy(lastName) && email && password && confirmPassword && password === confirmPassword && validateEmail(email) && validatePassword(password) === '' && agreeToTerms;
    };
    const isFormStepTwo = () => {
        const { companyName, companyNo, country } = formValues;
        return companyName &&
            companyNo &&
            country &&
            searchTerm &&
            !errors.companyName &&
            !errors.companyNo &&
            !errors.country;
    };

    // The API's limits, keyed by the form's fields (the API calls the country countryId).
    const checkLimits = (fields?: string[]) => {
        const values = { ...formValues, firstName: tidy(formValues.firstName), lastName: tidy(formValues.lastName), countryId: formValues.country };
        const { countryId, ...found } = validateAgainstLimits(values, limits, language, fields);
        return countryId ? { ...found, country: countryId } : found;
    };
    const handleSubmitStepOne = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        const stepErrors = checkLimits(["firstName", "lastName", "email", "password"]);
        if (Object.keys(stepErrors).length > 0) {
            setErrors(prev => ({ ...prev, ...stepErrors }));
            return;
        }
        setStep("2")
    }
    const handleSubmitStepTwo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        setApiError('');
        const limitErrors = checkLimits(["firstName", "lastName", "email", "password", "companyName", "companyNo", "countryId"]);
        if (Object.keys(limitErrors).length > 0) {
            setErrors(prev => ({ ...prev, ...limitErrors }));
            if (Object.keys(limitErrors).some((field) => SIGNUP_FIELD_STEPS[field] === 1)) setStep("1");
            return;
        }
        try {
            const payload = {
                // fullName keeps an older API working; the current one stores the two names as given.
                accountDetails: {
                    firstName: tidy(formValues.firstName),
                    lastName: tidy(formValues.lastName),
                    fullName: `${tidy(formValues.firstName)} ${tidy(formValues.lastName)}`,
                    email: formValues.email,
                    password: formValues.password
                },
                companyDetails: {
                    companyName: tidy(formValues.companyName),
                    companyNo: formValues.companyNo,
                    countryId: formValues.country
                }
            };
            const response = await signup(payload);
            if (response.data.success) {
                setShowSuccessPopup(true);
            } else {
                setApiError(response.data.message);
            }
        } catch (error: any) {
            showApiError(error, {
                language,
                setFieldErrors: (fe) => setErrors(prev => ({ ...prev, ...fe })),
                fieldAliases: SIGNUP_FIELD_ALIASES,
                fieldSteps: SIGNUP_FIELD_STEPS,
                onStep: (next) => setStep(String(next)),
            });
        }
    }
    return (
        <div className="py-16 h-full flex flex-col justify-between">
            <img src={logo} className="w-fit " />
            <div className="space-y-10">
                <p className="text-[32px] text-[#05292A] font-semibold font-inter">Sign up</p>
                <div className="flex items-center gap-x-6">
                    <div className="w-1/2 space-y-4">
                        <div
                            className="w-full"
                            style={{
                                height: '4px',
                                backgroundColor: (step === "1" || step === "2") ? '#0F7679' : '#DBE4E4',
                                borderRadius: '20px',
                            }}
                        ></div>
                        <div className="">
                            <p className="text-[14px] font-medium text-[#0F7679]">Step 1</p>
                            <p className="text-[14px] font-medium text-[#05292A]">Account details</p>
                        </div>
                    </div>
                    <div className="w-1/2 space-y-4">
                        <div
                            className="w-full"
                            style={{
                                height: '4px',
                                backgroundColor: (step === "2") ? '#0F7679' : '#DBE4E4',
                                borderRadius: '20px',
                            }}
                        ></div>
                        <div className="">
                            <p className="text-[14px] font-medium text-[#0F7679]">Step 2</p>
                            <p className="text-[14px] font-medium text-[#05292A]">Company details</p>
                        </div>
                    </div>
                </div>
                <div className="space-y-8">
                    {
                        step === "1" ?
                            <form className="w-full" onSubmit={(e: any) => handleSubmitStepOne(e)}>
                                <div className="space-y-3">
                                    <div className="space-y-8">
                                        {/* Two required fields: the account stores first and last name separately. */}
                                        <div className="w-full flex flex-col sm:flex-row gap-8 sm:gap-4">
                                            {(["firstName", "lastName"] as const).map((field) => (
                                                <div key={field} className="w-full space-y-2">
                                                    <label htmlFor={`signup-${field}`} className="block text-[14px] text-main font-medium no-select">
                                                        {t(field === "firstName" ? "First Name" : "Last Name")}
                                                    </label>
                                                    <input
                                                        id={`signup-${field}`}
                                                        name={field}
                                                        autoComplete={field === "firstName" ? "given-name" : "family-name"}
                                                        required
                                                        aria-invalid={!!errors[field]}
                                                        {...limitProps(limits, field)}
                                                        value={formValues[field]}
                                                        onChange={handleChange}
                                                        onBlur={handleNameBlur}
                                                        className={`${INPUT_TEXT} placeholder:text-subtitle text-subtitle font-medium border ${errors[field] ? 'border-red-500' : 'border-[#DBE4E4]'} px-4 py-[14px] rounded-xl outline-none focus-none bg-transparent w-full`}
                                                    />
                                                    <FieldCounter value={formValues[field]} max={limits[field]?.maxLength} />
                                                    {errors[field] && (
                                                        <p className="text-red-500 text-sm mt-1">{errors[field]}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="w-full space-y-2">
                                            <p className="text-[14px] text-main font-medium no-select">Email</p>
                                            <input
                                                name="email"
                                                type="email"
                                                inputMode="email"
                                                autoComplete="email"
                                                {...limitProps(limits, "email")}
                                                value={formValues.email}
                                                onChange={handleChange}
                                                className={`${INPUT_TEXT} placeholder:text-subtitle text-subtitle font-medium border ${errors.email ? 'border-red-500' : 'border-[#DBE4E4]'} px-4 py-[14px] rounded-xl outline-none focus-none bg-transparent w-full`}
                                            />
                                            <FieldCounter value={formValues.email} max={limits.email?.maxLength} />
                                            {errors.email && (
                                                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                                            )}
                                        </div>
                                        <div className="w-full space-y-2">
                                            <p className="text-[14px] text-main font-medium no-select">Password</p>
                                            <div className="relative">
                                                <input
                                                    name="password"
                                                    autoComplete="new-password"
                                                    value={formValues.password}
                                                    onChange={handleChange}
                                                    type={showPassword.password ? "text" : "password"}
                                                    className={`${INPUT_TEXT} placeholder:text-subtitle text-subtitle font-medium border ${errors.password ? 'border-red-500' : 'border-[#DBE4E4]'} px-4 py-[14px] rounded-xl outline-none focus-none bg-transparent w-full`}
                                                />
                                                <div className="absolute top-1/2 -translate-y-1/2 right-4">
                                                    <img
                                                        src={showPassword.password ? unView : view}
                                                        className="w-6 aspect-square cursor-pointer"
                                                        onClick={() => { setShowPassword({ ...showPassword, password: !showPassword.password }) }}
                                                    />
                                                </div>
                                            </div>
                                            {errors.password && (
                                                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                                            )}
                                        </div>
                                        <div className="w-full space-y-2">
                                            <p className="text-[14px] text-main font-medium no-select">Confirm Password</p>
                                            <div className="relative">
                                                <input
                                                    name="confirmPassword"
                                                    autoComplete="new-password"
                                                    value={formValues.confirmPassword}
                                                    onChange={handleChange}
                                                    type={showPassword.confirmPassword ? "text" : "password"}
                                                    className={`${INPUT_TEXT} placeholder:text-subtitle text-subtitle font-medium border ${errors.confirmPassword ? 'border-red-500' : 'border-[#DBE4E4]'} px-4 py-[14px] rounded-xl outline-none focus-none bg-transparent w-full`}
                                                />
                                                <div className="absolute top-1/2 -translate-y-1/2 right-4">
                                                    <img
                                                        src={showPassword.confirmPassword ? unView : view}
                                                        className="w-6 aspect-square cursor-pointer"
                                                        onClick={() => { setShowPassword({ ...showPassword, confirmPassword: !showPassword.confirmPassword }) }}
                                                    />
                                                </div>
                                            </div>
                                            {errors.confirmPassword && (
                                                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-full flex items-center justify-between">
                                        <div
                                            role="checkbox"
                                            aria-checked={formValues?.agreeToTerms}
                                            tabIndex={0}
                                            className="flex items-center gap-x-2 cursor-pointer"
                                            onClick={() => { setFormValues((prev) => ({ ...prev, agreeToTerms: !prev.agreeToTerms })) }}
                                            onKeyDown={(e) => {
                                                // Space toggles like a native checkbox; Enter submits the step, as from a field.
                                                if (e.key === " ") {
                                                    e.preventDefault();
                                                    setFormValues((prev) => ({ ...prev, agreeToTerms: !prev.agreeToTerms }));
                                                } else if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    if (isFormStepOne()) e.currentTarget.closest("form")?.requestSubmit();
                                                }
                                            }}
                                        >
                                            <div className="flex items-center gap-x-2"
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '6px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.2s',
                                                    backgroundColor: formValues?.agreeToTerms ? '#0F7679' : 'white',
                                                    border: formValues?.agreeToTerms ? 'none' : '2px solid #d1d5db',
                                                    color: 'white'
                                                }}
                                            >
                                                {formValues?.agreeToTerms && <CheckIconWhite />}
                                            </div>
                                            <p className="text-sm font-semibold text-[#222525] font-inter" >I agree to the <span className="underline decoration-solid underline-offset-0 decoration-0 text-[#0F7679]"> Terms of Use  </span> and <span className="underline decoration-solid underline-offset-0 decoration-0 text-[#0F7679]">  Privacy Policy</span> .</p>
                                        </div>
                                    </div>
                                </div>
                                <button type="submit" disabled={!isFormStepOne()} className={`mt-8 w-full h-10 text-sm font-medium flex items-center justify-center rounded-[30px] ${isFormStepOne() ? "bg-[#0F7679] text-white" : "bg-[#F8F8F8] text-[#C9D7D8]"}`}>
                                    Continue
                                </button>
                            </form>
                            :
                            <form className="w-full" onSubmit={(e: any) => handleSubmitStepTwo(e)}>
                                <div className="space-y-3">
                                    <div className="space-y-8">
                                        <div className="w-full space-y-2">
                                            <p className="text-[14px] text-main font-medium no-select">Company Name</p>
                                            <input
                                                name="companyName"
                                                autoComplete="organization"
                                                onBlur={handleNameBlur}
                                                {...limitProps(limits, "companyName")}
                                                value={formValues.companyName}
                                                onChange={handleChange}
                                                className={`${INPUT_TEXT} placeholder:text-subtitle text-subtitle font-medium border ${errors.companyName ? 'border-red-500' : 'border-[#DBE4E4]'} px-4 py-[14px] rounded-xl outline-none focus-none bg-transparent w-full`}
                                            />
                                            <FieldCounter value={formValues.companyName} max={limits.companyName?.maxLength} />
                                            {errors.companyName && (
                                                <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
                                            )}
                                        </div>
                                        <div className="w-full space-y-2">
                                            <p className="text-[14px] text-main font-medium no-select">Company No</p>
                                            <input
                                                name="companyNo"
                                                {...limitProps(limits, "companyNo")}
                                                value={formValues.companyNo}
                                                onChange={handleChange}
                                                className={`${INPUT_TEXT} placeholder:text-subtitle text-subtitle font-medium border ${errors.companyNo ? 'border-red-500' : 'border-[#DBE4E4]'} px-4 py-[14px] rounded-xl outline-none focus-none bg-transparent w-full`}
                                            />
                                            <FieldCounter value={formValues.companyNo} max={limits.companyNo?.maxLength} />
                                            {errors.companyNo && (
                                                <p className="text-red-500 text-sm mt-1">{errors.companyNo}</p>
                                            )}
                                        </div>
                                        <div className="w-full space-y-2">
                                            <p className="text-[14px] text-main font-medium no-select">Country</p>
                                            <div className="relative" ref={dropdownRef}>
                                                <input
                                                    type="text"
                                                    role="combobox"
                                                    aria-label="Country"
                                                    aria-expanded={isCountryDropdownOpen}
                                                    aria-activedescendant={isCountryDropdownOpen && highlightedCountry >= 0 ? `signup-country-option-${highlightedCountry}` : undefined}
                                                    aria-controls="signup-country-list"
                                                    aria-autocomplete="list"
                                                    autoComplete="off"
                                                    value={searchTerm}
                                                    onChange={(e) => {
                                                        setSearchTerm(e.target.value);
                                                        // Keep a country id only while the text names that country exactly.
                                                        const match = exactCountry(e.target.value);
                                                        setFormValues(prev => ({ ...prev, country: match?.id ?? "" }));
                                                        if (match) setErrors(prev => ({ ...prev, country: '' }));
                                                        setHighlightedCountry(-1);
                                                        setIsCountryDropdownOpen(true);
                                                    }}
                                                    onBlur={() => {
                                                        setIsCountryDropdownOpen(false);
                                                        setHighlightedCountry(-1);
                                                        if (searchTerm.trim() && !formValues.country) setErrors(prev => ({ ...prev, country: t('Country is required') }));
                                                    }}
                                                    onKeyDown={handleCountryKeyDown}
                                                    onClick={() => setIsCountryDropdownOpen(true)}
                                                    className={`${INPUT_TEXT} placeholder:text-subtitle text-subtitle font-medium border ${errors.country ? 'border-red-500' : 'border-[#DBE4E4]'
                                                        } px-4 py-[14px] rounded-xl outline-none focus-none bg-transparent w-full`}
                                                />
                                                {isCountryDropdownOpen && (
                                                    <div id="signup-country-list" ref={countryListRef} role="listbox" className="absolute z-10 w-full mt-1 bg-white border border-[#DBE4E4] rounded-xl shadow-lg max-h-60 overflow-auto">
                                                        {matchingCountries
                                                            .map((country, index) => (
                                                                <div
                                                                    key={country.id}
                                                                    id={`signup-country-option-${index}`}
                                                                    role="option"
                                                                    aria-selected={index === highlightedCountry}
                                                                    className={`px-4 py-3 cursor-pointer hover:bg-[#F9F9F9] text-main ${index === highlightedCountry ? 'bg-[#F0F7F7]' : ''}`}
                                                                    onMouseDown={(e) => e.preventDefault()}
                                                                    onClick={() => handleCountrySelect(country.id, country.name)}
                                                                >
                                                                    {country.name}
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                )}
                                                {errors.country && (
                                                    <p className="text-red-500 text-sm mt-1">{errors.country}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-x-4">
                                    {/* <button onClick={() => { setStep("1") }} className="mt-8 w-1/2 h-10 text-sm font-medium flex items-center justify-center rounded-xl bg-[#F9F9F9] text-main border border-[#DDDDE2]">
                                        Go Back
                                    </button>
                                    <button type="submit" disabled={!isFormStepTwo()} className={`mt-8 w-1/2 h-10 text-sm font-medium flex items-center justify-center rounded-xl ${isFormStepTwo() ? "bg-[#0F7679] text-white" : "bg-[#F8F8F8] text-[#C6C6C6]"}`}>
                                        Sign up
                                    </button> */}
                                    <button type="submit" disabled={!isFormStepTwo()} className={`mt-8 w-full h-10 text-sm font-medium flex items-center justify-center rounded-[30px] ${isFormStepTwo() ? "bg-[#0F7679] text-white" : "bg-[#F8F8F8] text-[#C9D7D8]"}`}>
                                        Sign up
                                    </button>
                                </div>
                                {apiError && (
                                    <p className="text-red-500 text-sm mt-4 text-center">{apiError}</p>
                                )}
                            </form>
                    }
                </div>
            </div>
            <div className="flex items-center justify-between">
                {
                    step === "2" &&
                    <div className="flex items-center cursor-pointer" onClick={() => { setStep("1") }}>
                        <div
                            className="w-6 h-6 flex items-center justify-center "
                        >
                            <BackIcon />
                        </div>
                        <p
                            style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#05292A' }}
                            className="font-inter"
                        >
                            Back
                        </p>
                    </div>
                }
                <div className="flex items-center gap-x-1 font-medium">
                    <p className="text-[#05292A] text-sm">Already have an account? </p>
                    <p className="cursor-pointer text-[#0F7679] text-sm" onClick={() => { nav("/login") }}>Sign in</p>
                </div>
            </div>
            {showSuccessPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4">
                        <h2 className="text-2xl font-medium text-main mb-4">Success!</h2>
                        <p className="text-main mb-6">Your account has been created successfully.</p>
                        <button
                            onClick={() => nav('/login')}
                            className="w-full bg-[#0F7679] text-white font-medium py-3 rounded-xl"
                        >
                            Continue to Sign in
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Signup;